export type TableColumn<Row> = {
  key: keyof Row | string;
  label: string;
  rawValue?: (row: Row) => unknown;
};

const stringifyDetail = (detail: unknown): string => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object') {
          const record = item as Record<string, unknown>;
          const location = Array.isArray(record.loc)
            ? record.loc.filter((part) => part !== 'body').join('.')
            : '';
          const message = stringifyDetail(record.msg ?? record.detail ?? item);
          return location ? `${location}: ${message}` : message;
        }
        return stringifyDetail(item);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>;
    if ('detail' in record) return stringifyDetail(record.detail);
    if ('message' in record) return stringifyDetail(record.message);
    return Object.entries(record)
      .map(([key, value]) => `${key}: ${stringifyDetail(value)}`)
      .join('; ');
  }
  return detail === null || detail === undefined ? '' : String(detail);
};

export const parseApiErrorResponse = async (
  response: Response,
  fallback = 'Request failed',
): Promise<string> => {
  const statusFallback = `${fallback} (${response.status})`;
  let text = '';
  try {
    text = await response.text();
  } catch {
    return statusFallback;
  }
  if (!text.trim()) return statusFallback;
  try {
    const parsed = JSON.parse(text);
    return stringifyDetail(parsed?.detail ?? parsed) || statusFallback;
  } catch {
    return text.trim() || statusFallback;
  }
};

export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message === 'Failed to fetch'
      ? 'Network request failed. Check your connection and try again.'
      : error.message;
  }
  const detail = stringifyDetail(error);
  return detail || fallback;
};

export const naturalCompare = (left: unknown, right: unknown): number =>
  String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

export const formatLast30DaysVolume = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number)}\u00A0m³` : 'Unavailable';
};

export const formatGlacierTotalVolume = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const number = Number(value);
  return Number.isFinite(number)
    ? `${Math.round(number)} m³`
    : 'Unavailable';
};

export const formatOneDecimal = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(1) : '—';
};

export const formatTwoSignificantFigures = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Unavailable';
  if (number === 0) return '0';

  const magnitude = Math.floor(Math.log10(Math.abs(number)));
  const place = 10 ** (magnitude - 1);
  return String(Math.round(number / place) * place);
};

export const formatFullVolume = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Unavailable';
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

export const averageMonthlyGrowthPerStupa = (
  combinedMonthlyAverage: unknown,
  stupaCount: number,
): number => {
  const total = Number(combinedMonthlyAverage);
  if (!Number.isFinite(total) || stupaCount <= 0) return 0;
  return total / stupaCount;
};

export const maxAvailableHarvestedHours = (
  values: unknown[],
): number | null => {
  const available = values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map(Number)
    .filter(Number.isFinite);
  return available.length ? Math.max(...available) : null;
};

export const formatHarvestedFreezingTime = (
  value: unknown,
): { primary: string; detail?: string } => {
  if (value === null || value === undefined || value === '') {
    return { primary: 'Delayed data' };
  }
  const hours = Number(value);
  if (!Number.isFinite(hours)) return { primary: 'Delayed data' };
  return { primary: `${Math.round(hours)}h` };
};

const tsvCell = (value: unknown): string =>
  String(value ?? '')
    .replace(/\t/g, ' ')
    .replace(/\r?\n/g, ' ');

export const rowsToTsv = <Row,>(
  rows: Row[],
  columns: TableColumn<Row>[],
): string => [
  columns.map((column) => tsvCell(column.label)).join('\t'),
  ...rows.map((row) =>
    columns.map((column) => {
      const value = column.rawValue
        ? column.rawValue(row)
        : (row as Record<string, unknown>)[String(column.key)];
      return tsvCell(value);
    }).join('\t')
  ),
].join('\n');

export const copyText = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Clipboard copy was not available');
};
